import {
  ANALYTICS_LIMITS,
  LIGHTS,
  type Light,
  type ManagerProject,
  type ManagerView,
  RATING_WEIGHTS,
} from '@epc/contracts';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import clsx from 'clsx';
import { useState } from 'react';
import { useCrumbs } from '../../layout/crumbs';
import { api } from '../../lib/api';
import { date, percent, plural, tengeShort } from '../../lib/format';
import { Badge, EmptyState, ErrorBox, Spinner, type Tone } from '../../ui/feedback';
import { Page, PageHeader, Panel, TitleBlock } from '../../ui/layout';
import { ProcurementOverviewStrip } from '../procurement/payments';
import { ProjectPanel } from './ProjectPanel';

export const managerQuery = {
  queryKey: ['analytics', 'manager'],
  queryFn: () => api<ManagerView>('/analytics/manager'),
};

export const LIGHT_TONE: Record<Light, Tone> = { ok: 'success', warning: 'warning', critical: 'danger' };

const idx = (v: number | null) => (v === null ? '—' : String(v).replace('.', ','));

function Hidden() {
  return <span className="text-ink-3">скрыто</span>;
}

/** Просрочка и срок: дни, без сумм — видно всем, у кого есть право на аналитику */
function Term({ p }: { p: ManagerProject }) {
  const d = p.time.worstDeviationDays;
  if (d === null) return <span className="text-ink-3">вех нет</span>;
  return (
    <span className={clsx('num', d > 0 && 'font-medium text-danger')}>
      {d > 0 ? `+${d} дн` : 'по плану'}
      {p.time.overdueMilestones ? (
        <span className="block text-[12px] font-normal text-ink-2">
          {p.time.overdueMilestones} {plural(p.time.overdueMilestones, 'веха', 'вехи', 'вех')} просрочено
        </span>
      ) : null}
    </span>
  );
}

function Kpis({ view }: { view: ManagerView }) {
  const t = view.totals;
  const m = t.money;
  return (
    <TitleBlock
      className="mb-6"
      items={[
        {
          label: 'Проекты',
          value: t.projects,
          note:
            t.critical || t.warning
              ? `плохо: ${t.critical}, внимание: ${t.warning}`
              : t.projects
                ? 'все в норме'
                : 'нет доступных проектов',
          tone: t.critical ? 'danger' : 'default',
        },
        {
          label: 'Сроки',
          value:
            t.worstDeviationDays === null
              ? '—'
              : t.worstDeviationDays > 0
                ? `+${t.worstDeviationDays} дн`
                : 'по плану',
          note: t.overdueMilestones ? `просрочено вех: ${t.overdueMilestones}` : 'просроченных вех нет',
          tone: t.worstDeviationDays && t.worstDeviationDays > 0 ? 'danger' : 'default',
        },
        {
          label: 'Деньги: оплачено',
          value: m && view.seesMargin ? percent(m.paidPct, 0) : <Hidden />,
          note:
            m && view.seesMargin && m.budget !== null
              ? `${tengeShort(m.paid)} из ${tengeShort(m.budget)}`
              : 'суммы открывает уровень доступа к деньгам',
        },
        {
          label: 'Маржа: план → прогноз',
          value:
            m && view.seesMargin ? (
              <span>
                {percent(m.planned?.marginPct ?? null)} → {percent(m.forecast?.marginPct ?? null)}
              </span>
            ) : (
              <Hidden />
            ),
          note:
            m?.marginDropPp !== null && m?.marginDropPp !== undefined
              ? m.marginDropPp > 0
                ? `прогноз ниже плана на ${String(m.marginDropPp).replace('.', ',')} п. п.`
                : 'прогноз не ниже плана'
              : view.seesMargin
                ? 'нужны смета и бюджет проекта'
                : 'суммы открывает уровень доступа к деньгам',
          tone:
            m?.marginDropPp && m.marginDropPp >= ANALYTICS_LIMITS.marginDropWarning ? 'danger' : 'default',
        },
        {
          label: 'Освоение: SPI / CPI',
          value:
            m?.ev && view.seesEv ? `${idx(m.ev.spi)} / ${idx(m.ev.cpi)}` : view.seesEv ? '—' : <Hidden />,
          note: m?.ev ? 'по базовым планам графиков' : 'нужен базовый план со стоимостью работ',
          tone:
            (m?.ev?.spi ?? 1) < ANALYTICS_LIMITS.indexWarning ||
            (m?.ev?.cpi ?? 1) < ANALYTICS_LIMITS.indexWarning
              ? 'danger'
              : 'default',
        },
      ]}
    />
  );
}

/** Рейтинг 0–100: число, шкала и слово — цвет не единственный признак */
function RatingCell({ p }: { p: ManagerProject }) {
  const r = p.rating;
  if (!r || r.score === null) return <span className="text-ink-3">нет данных</span>;
  const word = r.score >= 80 ? 'высокий' : r.score >= 60 ? 'средний' : 'низкий';
  return (
    <span className="flex flex-col gap-1">
      <span className="num font-medium">
        {r.score} из 100 <span className="text-[12px] font-normal text-ink-2">{word}</span>
      </span>
      <span className="block h-1.5 w-full max-w-24 rounded-full bg-tile" aria-hidden>
        <span
          className={clsx(
            'block h-1.5 rounded-full',
            r.score >= 80 ? 'bg-success' : r.score >= 60 ? 'bg-warning' : 'bg-danger',
          )}
          style={{ width: `${r.score}%` }}
        />
      </span>
      {r.withoutFinance ? <span className="text-[11px] text-ink-2">без финансов</span> : null}
    </span>
  );
}

/** Из чего сложился рейтинг выбранного проекта: показатели, веса и пояснения */
function RatingPanel({ p }: { p: ManagerProject }) {
  const r = p.rating;
  if (!r) return null;
  return (
    <Panel
      title={`Рейтинг проекта ${p.code}`}
      meta={r.score === null ? 'посчитать нечего' : `${r.score} из 100`}
      className="mb-6"
      bodyClassName="p-0"
    >
      <ul className="divide-y divide-line">
        {r.parts.map((x) => (
          <li
            key={x.key}
            className="grid gap-x-4 gap-y-0.5 px-4 py-3 text-[13px] md:grid-cols-[minmax(200px,2fr)_5rem_6rem_3fr]"
          >
            <span className="font-medium">{x.title}</span>
            <span className="text-ink-2">вес {x.weight ?? RATING_WEIGHTS[x.key]}%</span>
            <span className="num">{x.score === null ? 'не учтён' : `${Math.round(x.score)} из 100`}</span>
            <span className="text-ink-2">
              {x.detail ?? (x.score === null ? 'нет данных, вес перераспределён' : '')}
            </span>
          </li>
        ))}
      </ul>
      <p className="border-t border-line px-4 py-3 text-[12px] text-ink-2">
        Показатель без данных в расчёт не входит, остальные пересчитываются на 100 %.
        {r.withoutFinance
          ? ' Рейтинг посчитан без финансовой части (маржа, SPI, CPI): сравнивать его с полным рейтингом нельзя.'
          : ''}
      </p>
    </Panel>
  );
}

function ProjectRow({ p, selected, onPick }: { p: ManagerProject; selected: boolean; onPick: () => void }) {
  const m = p.money;
  return (
    <li>
      <button
        type="button"
        onClick={onPick}
        aria-pressed={selected}
        className={clsx(
          'grid w-full min-h-11 grid-cols-2 gap-x-4 gap-y-2 px-4 py-3 text-left transition-colors hover:bg-tile/50 md:grid-cols-[minmax(200px,2fr)_1.2fr_0.9fr_0.9fr_1.2fr_0.9fr_0.7fr] md:items-center',
          selected && 'bg-tile/70',
        )}
      >
        <span className="col-span-2 flex min-w-0 flex-col gap-1 md:col-span-1">
          <span className="flex flex-wrap items-center gap-2">
            <Badge tone={LIGHT_TONE[p.light]}>{LIGHTS[p.light]}</Badge>
            <span className="num text-[12px] text-ink-2">{p.code}</span>
          </span>
          <span className="text-[15px] font-medium [overflow-wrap:anywhere] text-ink">{p.name}</span>
          {p.reasons.length ? <span className="text-[12px] text-ink-2">{p.reasons.join('; ')}</span> : null}
        </span>
        <span className="flex flex-col gap-0.5 text-[13px]">
          <span className="text-[12px] text-ink-2 md:hidden">Сроки</span>
          <Term p={p} />
          {p.time.finishForecast && p.time.finishBaseline ? (
            <span className="text-[12px] text-ink-2">
              окончание {date(p.time.finishForecast)} (план {date(p.time.finishBaseline)})
            </span>
          ) : null}
        </span>
        <span className="flex flex-col gap-0.5 text-[13px]">
          <span className="text-[12px] text-ink-2 md:hidden">Оплачено</span>
          {m && m.paidPct !== null ? <span className="num">{percent(m.paidPct, 0)}</span> : <Hidden />}
        </span>
        <span className="flex flex-col gap-0.5 text-[13px]">
          <span className="text-[12px] text-ink-2 md:hidden">SPI / CPI</span>
          {m?.ev ? (
            <span
              className={clsx(
                'num',
                ((m.ev.spi ?? 1) < ANALYTICS_LIMITS.indexWarning ||
                  (m.ev.cpi ?? 1) < ANALYTICS_LIMITS.indexWarning) &&
                  'font-medium text-danger',
              )}
            >
              {idx(m.ev.spi)} / {idx(m.ev.cpi)}
            </span>
          ) : (
            <span className="text-ink-3">—</span>
          )}
        </span>
        <span className="flex flex-col gap-0.5 text-[13px]">
          <span className="text-[12px] text-ink-2 md:hidden">Маржа, план → прогноз</span>
          {m?.planned || m?.forecast ? (
            <span className="num">
              {percent(m.planned?.marginPct ?? null)} → {percent(m.forecast?.marginPct ?? null)}
            </span>
          ) : (
            <Hidden />
          )}
        </span>
        <span className="flex flex-col gap-0.5 text-[13px]">
          <span className="text-[12px] text-ink-2 md:hidden">Рейтинг</span>
          <RatingCell p={p} />
        </span>
        <span className="hidden text-[13px] text-accent-text md:block">
          {selected ? 'Открыт ниже' : 'Подробнее'}
        </span>
      </button>
    </li>
  );
}

/** Экран руководителя (6.18): за 1–2 минуты — закупки, деньги, сроки и маржа по проектам, ниже — разбор выбранного */
export function ManagerPage() {
  useCrumbs([]);
  const search = useSearch({ strict: false }) as { project?: string; tab?: string };
  const navigate = useNavigate();
  const q = useQuery(managerQuery);
  const [sortBy, setSortBy] = useState<'code' | 'rating'>('code');

  if (q.isPending) return <Spinner />;
  if (q.isError)
    return (
      <Page>
        <PageHeader title="Руководителю" />
        <ErrorBox error={q.error} />
      </Page>
    );
  const view = q.data;
  const selectedId =
    view.projects.find((p) => p.id === search.project)?.id ??
    (view.projects.find((p) => p.light !== 'ok') ?? view.projects[0])?.id;
  const shown =
    sortBy === 'rating'
      ? [...view.projects].sort((a, b) => (a.rating?.score ?? 101) - (b.rating?.score ?? 101))
      : view.projects;
  const selected = view.projects.find((p) => p.id === selectedId);
  const pick = (id: string) =>
    navigate({ to: '/analytics', search: { project: id, tab: search.tab } as never });

  return (
    <Page>
      <PageHeader
        title="Руководителю"
        subtitle={`Закупки, деньги, сроки и маржа на ${date(view.asOf)}. Ниже — разбор выбранного проекта.`}
      />
      <ProcurementOverviewStrip />
      <Kpis view={view} />
      {!view.seesMargin ? (
        <p className="mb-6 rounded-lg border border-line bg-surface px-5 py-3 text-[14px] text-ink-2">
          Суммы, маржа и освоенный объём в деньгах скрыты: их открывает уровень доступа к деньгам, его
          назначает владелец компании. Сроки и закупки без сумм видны.
        </p>
      ) : null}
      <Panel
        title="Проекты"
        meta={view.more ? 'показаны первые проекты по коду' : undefined}
        actions={
          <button
            type="button"
            onClick={() => setSortBy(sortBy === 'code' ? 'rating' : 'code')}
            className="inline-flex h-11 items-center rounded-md border border-line-strong px-3 text-[13px] hover:underline md:h-8"
          >
            {sortBy === 'code' ? 'Сначала худшие по рейтингу' : 'По коду проекта'}
          </button>
        }
        className="mb-6"
        bodyClassName="p-0"
      >
        {view.projects.length ? (
          <>
            <div className="hidden border-b border-line px-4 py-2 text-[12px] text-ink-2 md:grid md:grid-cols-[minmax(200px,2fr)_1.2fr_0.9fr_0.9fr_1.2fr_0.9fr_0.7fr] md:gap-x-4">
              <span>Проект</span>
              <span>Сроки</span>
              <span>Оплачено</span>
              <span>SPI / CPI</span>
              <span>Маржа, план → прогноз</span>
              <span>Рейтинг</span>
              <span />
            </div>
            <ul className="divide-y divide-line">
              {shown.map((p) => (
                <ProjectRow key={p.id} p={p} selected={p.id === selectedId} onPick={() => pick(p.id)} />
              ))}
            </ul>
          </>
        ) : (
          <EmptyState title="Нет проектов">
            Здесь появятся проекты, в которых у вас есть право «Видеть аналитику».
          </EmptyState>
        )}
        <p className="border-t border-line px-4 py-3 text-[12px] text-ink-2">
          Как читать цвет: «Внимание» — SPI или CPI ниже{' '}
          {String(ANALYTICS_LIMITS.indexWarning).replace('.', ',')}, маржа прогноза ниже плановой на{' '}
          {ANALYTICS_LIMITS.marginDropWarning} п. п., веха просрочена; «Плохо» — ниже{' '}
          {String(ANALYTICS_LIMITS.indexCritical).replace('.', ',')}, ниже на{' '}
          {ANALYTICS_LIMITS.marginDropCritical} п. п., просрочка от {ANALYTICS_LIMITS.overdueCritical} дней.
          Все цифры считает одна функция портала, поэтому они совпадают с разделами «Бюджет», «График» и
          «Портфель».
        </p>
      </Panel>
      {selected ? <RatingPanel p={selected} /> : null}
      {selectedId ? (
        <ProjectPanel
          projectId={selectedId}
          tab={search.tab}
          onTab={(tab) => navigate({ to: '/analytics', search: { project: selectedId, tab } as never })}
        />
      ) : null}
      <Link
        to="/"
        className="mt-6 inline-flex min-h-11 items-center text-[14px] text-accent-text underline-offset-4 hover:underline"
      >
        Все проекты, бюджет и график — в портфеле
      </Link>
    </Page>
  );
}
